import { useState, useEffect, useRef, useMemo } from 'react'
import { Drawer, Box, Text, Badge, TextInput, Group, Button, Loader, Stack } from '@mantine/core'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import type { Part } from '../../app/models/part'
import { ZONE_CATEGORY_MAP } from './zoneCategoryMap'

interface Props {
  buildId: string;
  zone: string;
  open: boolean;
  onClose: () => void;
  onPartAdded: () => void;
  currentMountType?: string | null;
  isAk?: boolean;
}

function fmtValue (v: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(v)
}

export default function AddPartPanel ({ buildId, zone, open, onClose, onPartAdded, currentMountType, isAk }: Props) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [allParts, setAllParts] = useState<Part[]>([])
  const [loading, setLoading] = useState(false)
  const [addingId, setAddingId] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const categories = ZONE_CATEGORY_MAP[zone]?.categories ?? []

  // Fetch all approved parts on open; category filter applied client-side
  // (the /parts endpoint has no category query param)
  useEffect(() => {
    if (!open) return
    setSearch('')
    setDebouncedSearch('')
    setLoading(true)
    agent.Parts.list({ pageSize: 500 })
      .then((r) => setAllParts(r.items.filter((p) => p.status === 1)))
      .catch(() => setAllParts([]))
      .finally(() => setLoading(false))
  }, [open])

  // Debounce search input (300 ms)
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(debounceRef.current)
  }, [search])

  // Filter: zone categories + search term (client-side)
  const filtered = useMemo(() => {
    let list = allParts
    if (categories.length > 0) {
      list = list.filter((p) => categories.includes(p.category ?? ''))
    }
    const q = debouncedSearch.trim().toLowerCase()
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.manufacturer ?? '').toLowerCase().includes(q) ||
          (p.partNumber ?? '').toLowerCase().includes(q)
      )
    }
    return list
  }, [allParts, categories, debouncedSearch])

  const handleAdd = async (partId: string) => {
    setAddingId(partId)
    try {
      await agent.Builds.addPart(buildId, partId, undefined, 1, zone)
      toast.success(`Part added to ${zone}`)
      onPartAdded()
    } catch {
      /* global interceptor handles error toasts */
    } finally {
      setAddingId(null)
    }
  }

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      title={
        <Box>
          <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 18, color: 'var(--gs-text)' }}>
            Add to {zone}
          </Text>
          {categories.length > 0 && (
            <Group spacing={4} mt={4}>
              {categories.map((cat) => (
                <Badge
                  key={cat}
                  size="xs"
                  variant="outline"
                  sx={{ borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' }}
                >
                  {cat}
                </Badge>
              ))}
            </Group>
          )}
        </Box>
      }
      position="right"
      size="md"
      padding="xl"
      styles={{
        drawer: { background: 'var(--gs-surface)', borderLeft: '1px solid var(--gs-border)' },
        header: { background: 'var(--gs-surface)', borderBottom: '1px solid var(--gs-border)', paddingBottom: 12 },
        title: { width: '100%' }
      }}
    >
      <Stack spacing="sm">
        <TextInput
          placeholder="Search by name or manufacturer…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          styles={{
            input: {
              background: 'var(--gs-surface2)',
              borderColor: 'var(--gs-border)',
              color: 'var(--gs-text)',
              '&::placeholder': { color: 'var(--gs-muted)' }
            }
          }}
        />

        {loading
          ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 'var(--gs-xl)' }}>
            <Loader size="sm" color="orange" />
          </Box>
            )
          : filtered.length === 0
            ? (
          <Box sx={{ textAlign: 'center', padding: 'var(--gs-xl) 0' }}>
            <Text size="sm" sx={{ color: 'var(--gs-muted)', marginBottom: 10 }}>
              No parts found for this zone.
            </Text>
            <Text
              component={Link}
              to="/parts/new"
              size="sm"
              sx={{
                color: 'var(--gs-accent)',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Submit a part →
            </Text>
          </Box>
              )
            : (
          <Box sx={{ border: '1px solid var(--gs-border)', borderRadius: 6, overflow: 'hidden' }}>
            {filtered.map((p) => (
              <Box
                key={p.id}
                sx={{
                  padding: 'var(--gs-sm) var(--gs-md)',
                  borderBottom: '1px solid var(--gs-border)',
                  '&:last-child': { borderBottom: 'none' },
                  background: 'var(--gs-surface)',
                  transition: 'background 0.1s',
                  '&:hover': { background: 'var(--gs-surface2)' }
                }}
              >
                <Group position="apart" noWrap>
                  <Box sx={{ minWidth: 0 }}>
                    <Text size="sm" weight={600} sx={{ color: 'var(--gs-text)' }} lineClamp={1}>
                      {p.name}
                    </Text>
                    <Text size="xs" sx={{ color: 'var(--gs-muted)' }}>
                      {[p.manufacturer, p.partNumber].filter(Boolean).join(' · ')}
                      {p.value > 0 && (
                        <Text component="span" sx={{ color: 'var(--gs-gold)', marginLeft: 8 }}>
                          {fmtValue(p.value)}
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    loading={addingId === p.id}
                    disabled={!!addingId && addingId !== p.id}
                    onClick={() => handleAdd(p.id)}
                    sx={{
                      flexShrink: 0,
                      background: 'var(--gs-accent)',
                      color: '#fff',
                      '&:hover:not(:disabled)': { background: 'var(--gs-accent-hover)' }
                    }}
                  >
                    Add to Build
                  </Button>
                </Group>
              </Box>
            ))}
          </Box>
              )}
      </Stack>
    </Drawer>
  )
}
